import {
	Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
	ChangeDetectorRef, ChangeDetectionStrategy, SimpleChange
} from '@angular/core';
import { TreeNode } from 'primeng/api';
import { ComponentBase } from 'common';
import { DiscResultsService } from 'discovery/services/discresults.service';
import { PrtgGroupNodeDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgGroupNodeDto';

// Modal picker for browsing the PRTG group hierarchy of a selected PRTG agent.
// Invoked from the Export-to-PRTG dialog's "Browse..." buttons. Emits the
// chosen group's ID + name on (onSelect).
@Component({
	selector: 'prtg-group-picker',
	templateUrl: './prtg-group-picker.component.html',
	styleUrls: ['./prtg-group-picker.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrtgGroupPickerComponent extends ComponentBase implements OnChanges {

	@Input() public display: boolean;
	@Input() public netViewGuid: string;
	@Input() public prtgSettingsGuid: string;
	@Input() public title: string;

	@Output() public displayChange = new EventEmitter<boolean>();
	@Output() public onCancel = new EventEmitter<void>();
	@Output() public onSelect = new EventEmitter<{ id: number, name: string }>();

	public treeNodes: TreeNode[] = [];
	public selectedNode: TreeNode | null = null;
	public loading: boolean = false;
	public errorMessage: string = "";

	constructor(
		private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {
		super();
		this.display = false;
		this.netViewGuid = "";
		this.prtgSettingsGuid = "";
		this.title = "Select PRTG Group";
	}

	public ngOnChanges(changes: SimpleChanges): void {
		const displayChange: SimpleChange = changes["display"];
		// Reload each time the picker is shown so the tree reflects the latest
		// PRTG state. Both netViewGuid and prtgSettingsGuid must be set.
		if (displayChange && displayChange.currentValue === true && this.netViewGuid && this.prtgSettingsGuid) {
			this.loadGroups();
		}
	}

	private loadGroups(): void {
		this.loading = true;
		this.errorMessage = "";
		this.treeNodes = [];
		this.selectedNode = null;
		this.changeDetector.markForCheck();

		this.discResultsService.listPrtgGroups(this.netViewGuid, this.prtgSettingsGuid).subscribe(
			(roots: PrtgGroupNodeDto[]) => {
				this.loading = false;
				if (!roots || roots.length === 0) {
					this.errorMessage = "No PRTG groups returned by the server.";
				} else {
					this.treeNodes = roots.map(r => this.toTreeNode(r));
					// Expand the first level so the user sees the structure
					// without having to click each root open.
					for (const n of this.treeNodes) n.expanded = true;
				}
				this.changeDetector.markForCheck();
			},
			(error) => {
				this.loading = false;
				this.errorMessage = "Failed to load PRTG groups.";
				this.changeDetector.markForCheck();
			});
	}

	private toTreeNode(dto: PrtgGroupNodeDto): TreeNode {
		const children = (dto.child_groups || []).map(c => this.toTreeNode(c));
		return {
			label: dto.name || ("(group " + dto.id + ")"),
			data: dto,
			leaf: children.length === 0,
			children: children,
			expandedIcon: "fa fa-folder-open",
			collapsedIcon: "fa fa-folder",
		};
	}

	public get enableSelect(): boolean {
		return !this.loading && this.selectedNode != null;
	}

	public hide(): void {
		this.display = false;
		this.displayChange.emit(this.display);
		this.changeDetector.markForCheck();
	}

	public cancel(): void {
		this.hide();
		this.onCancel.emit();
	}

	public select(): void {
		if (!this.enableSelect || !this.selectedNode) return;
		const dto: PrtgGroupNodeDto = this.selectedNode.data;
		this.hide();
		this.onSelect.emit({ id: dto.id, name: dto.name });
	}

	public onNodeDoubleClick(): void {
		this.select();
	}
}
